import {
    createAsyncThunk,
    createEntityAdapter,
    createSlice,
    PayloadAction
} from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';

import * as api from '../services/api';
import { RootState } from '../store';
import { initUserRatings, RatingSubject, UserRating, UserRatings } from '../types/rating';
import {
    WorkItem,
    WorkRecord,
    WorkReviewStatus,
    WorkType } from '../types/work';
import { isUserOffline } from '../utils/userStatus';

const workAdapter = createEntityAdapter();

export type TaskAnalysisRequestParams = {
    title: string;
    description: string;
    type?: string;
};

export interface TaskLongShortGeneratorParams {
    short_task_description: string;
    project: string;
    type?: string;
}

interface WorkState {
    work: WorkItem | null;
    workLoading: boolean;
    workError: string | null;
    workFetched: boolean;
    workActionError: string | null;
    workActive: boolean;
    workStartTimeEpochMs: number;
    workRatings: UserRatings;
    workRatingAuthorizationCode: string | null;
    workRecordRatings: UserRatings;
    workRecordRatingAuthorizationCode: string | null;
    workDone: boolean;
    activeWorkRecord: WorkRecord | null;
    workToken: string | null;
    taskFeedback: string[];
    feedbackLoading: boolean;
}

const initialState = workAdapter.getInitialState({
    work: null,
    workLoading: false,
    workError: null,
    workFetched: false,
    workActionError: null,
    workActive: false,
    workStartTimeEpochMs: 0,
    workRatings: {} as UserRatings,
    workRatingAuthorizationCode: null,
    workRecordRatings: {} as UserRatings,
    workRecordRatingAuthorizationCode: null,
    workDone: false,
    activeWorkRecord: null,
    workToken: null,
    taskFeedback: [],
    feedbackLoading: false
} as WorkState);

export const loadAvailableWork = createAsyncThunk(
    'work/loadAvailableWork',
    async (
        options: { currentWorkId?: number, specificWorkId?: number },
        { rejectWithValue }
    ) => {
        try {
            let fetchWorkResponse;

            if (options.specificWorkId) {
                fetchWorkResponse = await api.fetchSpecificWork(options.specificWorkId);
            } else {
                fetchWorkResponse = await api.fetchAvailableWork(options.currentWorkId);
            }

            const userOffline = isUserOffline(fetchWorkResponse.userStatus);

            return {
                work: userOffline ? null : fetchWorkResponse.work,
                workRecord: userOffline ? null : fetchWorkResponse.workRecord,
                workToken: userOffline ? null : fetchWorkResponse.workToken,
                userOffline
            };
        } catch (err: any) {
            // 404 just means that there is no available work at the moment
            if (err.status === 404) {
                return { work: null };
            } else if (err.error === 'user_offline' || err.description === 'user_offline') {
                return { work: null, userOffline: true };
            } else {
                return rejectWithValue(err);
            }
        }
    }
);

export const activateWork = createAsyncThunk(
    'work/activateWork',
    async (
        options: { workId: number, workType: WorkType, startTimeEpochMs: number, tzName: string },
        { rejectWithValue }
    ) => {
        try {
            return await api.startWork(
                options.workId, options.startTimeEpochMs, options.tzName
            );
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const checkActiveWork = createAsyncThunk(
    'work/checkActiveWork',
    async (_, { rejectWithValue }) => {
        try {
            return await api.checkActiveWork();
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const skipWork = createAsyncThunk(
    'work/skipWork',
    async (
        options: { workId: number, startTimeEpochMs: number, tzName: string },
        { rejectWithValue }
    ) => {
        try {
            const skipResponse = await api.skipWork(
                options.workId, options.startTimeEpochMs, options.tzName
            );
            
            return skipResponse;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const checkpointWork = createAsyncThunk(
    'work/checkpointWork',
    (options: {
        workId: number,
        durationSeconds: number
    }) => {
        return api.checkpointWork(
            options.workId,
            options.durationSeconds
        );
    }
);

export const cancelWork = createAsyncThunk(
    'work/cancelWork',
    async (options: { workId: number, durationSeconds: number, blockerDescription?: string, attachmentList?: number[] }, { rejectWithValue }) => {
        try {
            // notify finish work with no parameters (aka cancelled)
            return await api.finishWork(
                options.workId,
                options.durationSeconds,
                undefined,
                undefined,
                undefined,
                undefined,
                options.attachmentList,
                undefined,
                options.blockerDescription
            );
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const submitWorkSolution = createAsyncThunk(
    'work/submitWorkSolution',
    async (options: {
        workId: number,
        durationSeconds: number,
        reviewStatus?: WorkReviewStatus,
        reviewFeedback?: string,
        solutionUrls?: string[],
        attachmentList?: number[],
        solveByReviewer?: boolean
    }, { rejectWithValue }) => {
        try {
            return await api.finishWork(
                options.workId,
                options.durationSeconds,
                undefined,
                options.reviewStatus,
                options.reviewFeedback,
                options.solutionUrls,
                options.attachmentList,
                options.solveByReviewer
            );
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const submitWorkFeedback = createAsyncThunk(
    'work/submitWorkFeedback',
    async (options: {
        workId: number,
        durationSeconds: number,
        feedback: string
    }, { rejectWithValue }) => {
        try {
            return await api.finishWork(
                options.workId,
                options.durationSeconds,
                options.feedback
            );
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const submitWorkSolutionUrl = createAsyncThunk(
    'work/submitWorkSolutionUrl',
    async (options: {
        workId: number,
        durationSeconds: number,
        solutionUrls: string[],
        attachmentList?: number[]
    }, { rejectWithValue }) => {
        try {
            return await api.finishWork(
                options.workId,
                options.durationSeconds,
                undefined,
                undefined,
                undefined,
                options.solutionUrls,
                options.attachmentList
            );
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const submitWorkRating = createAsyncThunk(
    'work/submitWorkRating',
    async (options: {
        code: string,
        subject: RatingSubject,
        rating: UserRating
    }, { rejectWithValue }) => {
        try {
            return await api.updateRating(
                options.code,
                options.subject,
                options.rating
            );
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const submitWorkTypeCorrection = createAsyncThunk(
    'work/submitWorkTypeCorrection',
    async (options: {
        input: any,
        predictedOutput: string,
        correctedOutput: string
    }, { rejectWithValue }) => {
        try {
            return await api.updateWorkTypeClassification(
                options.input,
                options.predictedOutput,
                options.correctedOutput
            );
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const getDescriptionAnalysis = createAsyncThunk(
    'work/getDescriptionAnalysis',
    async (options: TaskAnalysisRequestParams, { rejectWithValue }) => {
        try {
            const response = await api.getTaskDescriptionAnalysis(options);
            return response || null;

        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const queryShortLongModel = createAsyncThunk(
    'work/queryShortLongModel',
    async (options: TaskLongShortGeneratorParams, { rejectWithValue }) => {
        try {
            const response = await api.getTaskDescriptionGenerator(options);
            return response['output'] || null;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const getFileViewUrl = createAsyncThunk(
    'attachment/getFileViewUrl',
    async (options: {
        fileId: number
    }, { rejectWithValue }) => {
        try {
            const response = await api.getFileViewUrl(options.fileId);
            return response;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const scanFile = createAsyncThunk(
    'attachment/scanFile',
    async (options: {
        fileId: number
    }, { rejectWithValue }) => {
        try {
            const response = await api.scanUserAttachment(options.fileId);
            return response;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

const workSlice = createSlice({
    name: 'work',
    initialState,
    reducers: {
        clearAvailableWorkError(state) {
            state.workError = null;
        },
        clearWorkActionError(state) {
            state.workActionError = null;
        },
        setWorkActionError(state, action: PayloadAction<string>) {
            state.workActionError = action.payload;
        },
        setWorkRatings(state, action: PayloadAction<UserRatings>) {
            state.workRatings = action.payload;
        },
        setWorkRecordRatings(state, action: PayloadAction<UserRatings>) {
            state.workRecordRatings = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(loadAvailableWork.pending, (state) => {
            state.workLoading = true;
            state.workError = null;
        });
        builder.addCase(loadAvailableWork.fulfilled, (state, action: any) => {
            state.workLoading = false;
            state.workFetched = true;

            if (action.payload.userOffline) {
                state.work = null;
                state.workError = 'user_offline';
                state.workActive = false;
                state.workDone = false;
                state.activeWorkRecord = null;
                state.workToken = null;
                state.workRatings = {} as UserRatings;
                state.workRatingAuthorizationCode = null;
                return;
            }

            state.work = action.payload.work;
            state.workRatings = action.payload.work?.ratingSubjects ? initUserRatings(action.payload.work?.ratingSubjects): {} as UserRatings;
            state.workRatingAuthorizationCode = action.payload.work?.ratingAuthorizationCode;
            state.workError = null;
            state.workActive = false;
            state.workDone = false;
            state.activeWorkRecord = null;

            // activate work if a work record was fetched (meaning the user already has
            // an active work item)
            if (action.payload.workRecord) {
                state.activeWorkRecord = action.payload.workRecord;
                state.workStartTimeEpochMs = action.payload.workRecord.startTimeEpochMs;
                state.workActive = true;
                state.workToken = action.payload.workToken || null;

                // override rating for this work record 
                if (action.payload.workRecord.ratingSubjects) {
                    state.workRecordRatings = initUserRatings(action.payload.workRecord.ratingSubjects);
                }
                if (action.payload.workRecord.ratingAuthorizationCode) {
                    state.workRecordRatingAuthorizationCode = action.payload.workRecord.ratingAuthorizationCode;
                }  
            }
        });
        builder.addCase(loadAvailableWork.rejected, (state, action: any) => {
            state.work = null;
            state.workLoading = false;
            state.workError = action.payload.error;
            state.workFetched = true;
            state.workRatings = {} as UserRatings;
            state.workRatingAuthorizationCode = null;
        });
        builder.addCase(activateWork.pending, (state) => {
            state.workLoading = true;
            state.workActionError = null;
        });
        builder.addCase(activateWork.fulfilled, (state, action) => {
            state.workLoading = false;
            state.workActive = true;
            state.workStartTimeEpochMs = action.meta.arg.startTimeEpochMs;

            if (action.payload.workRecord) {
                state.activeWorkRecord = action.payload.workRecord;
                state.workToken = action.payload.workToken || null;
                
                if (state.work && action.payload.workRecord?.ratingSubjects && action.payload.workRecord?.ratingAuthorizationCode) {
                    state.workRecordRatings = initUserRatings(action.payload.workRecord.ratingSubjects);
                    state.workRecordRatingAuthorizationCode = action.payload.workRecord.ratingAuthorizationCode;
                }
            }
        });
        builder.addCase(activateWork.rejected, (state, action: any) => {
            state.workLoading = false;
            if (action.payload.description === 'user_offline' || action.payload.error === 'user_offline') {
                state.workActionError = 'user_offline';
            } else {
                state.workActionError = action.payload.error;
            }
        });
        /* we currently don't indicate that a checkpoint is taking place. maybe in the future */
        /*builder.addCase(checkpointWork.pending, (state) => {
        });
        builder.addCase(checkpointWork.fulfilled, (state) => {
        });
        builder.addCase(checkpointWork.rejected, (state) => {
        });*/
        builder.addCase(cancelWork.pending, (state) => {
            state.workLoading = true;
            state.workActionError = null;
        });
        builder.addCase(cancelWork.fulfilled, (state) => {
            state.workLoading = false;
            state.workActive = false;
            state.activeWorkRecord = null;
            state.workToken = null;
        });
        builder.addCase(cancelWork.rejected, (state, action: any) => {
            state.workLoading = false;
            state.workActionError = action.payload.error;
        });
        builder.addCase(submitWorkSolution.pending, (state) => {
            state.workLoading = true;
            state.workActionError = null;
        });
        builder.addCase(submitWorkSolution.fulfilled, (state) => {
            state.workLoading = false;
            state.workDone = true;
            state.activeWorkRecord = null;
        });
        builder.addCase(submitWorkSolution.rejected, (state, action: any) => {
            state.workLoading = false;
            if (action.payload.description === 'conflicts_not_resolved') {
                state.workActionError = 'conflicts_not_resolved';
            } else {
                state.workActionError = action.payload.error;
            }
        });
        builder.addCase(submitWorkFeedback.pending, (state) => {
            state.workLoading = true;
            state.workActionError = null;
        });
        builder.addCase(submitWorkFeedback.fulfilled, (state) => {
            state.workLoading = false;
            state.workDone = true;
            state.workActive = false;
        });
        builder.addCase(submitWorkFeedback.rejected, (state, action: any) => {
            state.workLoading = false;
            state.workActionError = action.payload.error;
        });
        builder.addCase(submitWorkSolutionUrl.pending, (state) => {
            state.workLoading = true;
            state.workActionError = null;
        });
        builder.addCase(submitWorkSolutionUrl.fulfilled, (state) => {
            state.workLoading = false;
            state.workDone = true;
            state.workActive = false;
            state.activeWorkRecord = null;
        });
        builder.addCase(submitWorkSolutionUrl.rejected, (state, action: any) => {
            state.workLoading = false;
            state.workActionError = action.payload.error;
        });   
        builder.addCase(submitWorkRating.pending, (state) => {
            state.workLoading = true;
        });
        builder.addCase(submitWorkRating.fulfilled, (state) => {
            state.workLoading = false;
        });
        builder.addCase(submitWorkRating.rejected, (state) => {
            state.workLoading = false;
        });
        builder.addCase(skipWork.pending, (state) => {
            state.workLoading = true;
        });
        builder.addCase(skipWork.fulfilled, (state) => {
            state.workLoading = false;
        });
        builder.addCase(skipWork.rejected, (state) => {
            state.workLoading = false;
        });
        builder.addCase(submitWorkTypeCorrection.pending, (state) => {
            state.workLoading = true;
        });
        builder.addCase(submitWorkTypeCorrection.fulfilled, (state) => {
            state.workLoading = false;
        });
        builder.addCase(submitWorkTypeCorrection.rejected, (state, action: any) => {
            state.workActionError = action.payload.error;
        });
        builder.addCase(getDescriptionAnalysis.pending, (state) => {
            state.feedbackLoading = true;
        });
        builder.addCase(getDescriptionAnalysis.fulfilled, (state, action) => {
            state.feedbackLoading = false;
            state.taskFeedback = action.payload.output?.feedback;
        });
        builder.addCase(getDescriptionAnalysis.rejected, (state, action: any) => {
            state.feedbackLoading = action.payload.error;
        });
    }
});

export const {
    clearAvailableWorkError,
    clearWorkActionError,
    setWorkActionError,
    setWorkRatings,
    setWorkRecordRatings
} = workSlice.actions;

export default workSlice.reducer;

export interface RepositoryType{
    repoId: number,
    repoName: string,
    repoUrl: string
}

interface WorkSelectorsType {
    work: WorkItem | null;
    workLoading: boolean;
    workError: string | null;
    workFetched: boolean;
    workActionError: string | null;
    workActive: boolean;
    workStartTimeEpochMs: number;
    workRatings: UserRatings;
    workRatingAuthorizationCode: string;
    workRecordRatings: UserRatings;
    workRecordRatingAuthorizationCode: string;
    workDone: boolean;
    activeWorkRecord: WorkRecord;
    workToken: string | null;
    workRepositories: RepositoryType[];
    taskFeedback: string[];
    feedbackLoading: boolean;
}

export const WorkSelectors = (): WorkSelectorsType => {
    const work = useSelector(
        (state: RootState) => state.work.work
    );

    const workLoading = useSelector(
        (state: RootState) => state.work.workLoading
    );

    const workError = useSelector(
        (state: RootState) => state.work.workError
    );

    const workFetched = useSelector(
        (state: RootState) => state.work.workFetched
    );

    const workActionError = useSelector(
        (state: RootState) => state.work.workActionError
    );

    const workActive = useSelector(
        (state: RootState) => state.work.workActive
    );

    const workDone = useSelector(
        (state: RootState) => state.work.workDone
    );

    const workStartTimeEpochMs = useSelector(
        (state: RootState) => state.work.workStartTimeEpochMs
    );

    const workRatings = useSelector(
        (state: RootState) => state.work.workRatings
    );

    const workRatingAuthorizationCode = useSelector(
        (state: RootState) => state.work.workRatingAuthorizationCode
    );

    const workRecordRatings = useSelector(
        (state: RootState) => state.work.workRecordRatings
    );

    const workRecordRatingAuthorizationCode = useSelector(
        (state: RootState) => state.work.workRecordRatingAuthorizationCode
    );

    const activeWorkRecord = useSelector(
        (state: RootState) => state.work.activeWorkRecord
    );

    const workToken = useSelector(
        (state: RootState) => state.work.workToken
    );

    const workRepositories = useSelector(
        (state: RootState) => state.work?.work?.reposInfo
    );

    const taskFeedback = useSelector(
        (state: RootState) => state.work.taskFeedback
    );

    const feedbackLoading = useSelector(
        (state: RootState) => state.work.feedbackLoading
    );

    return {
        work,
        workLoading,
        workError,
        workFetched,
        workActionError,
        workActive,
        workDone,
        workStartTimeEpochMs,
        workRatings,
        workRatingAuthorizationCode,
        workRecordRatings,
        workRecordRatingAuthorizationCode,
        activeWorkRecord,
        workToken,
        workRepositories,
        taskFeedback,
        feedbackLoading
    };
};
